import { useCallback, useEffect, useRef, useState } from "react";
import type { PublicRoomView } from "@secret-cabinet/contracts";

export const SOUND_CATALOG = {
  "vote-ja": "A bright paper-chime when a revealed ballot is Ja.",
  "vote-nein": "A low paper-thump when a revealed ballot is Nein.",
  "policy-liberal": "A warm ascending chord when a Liberal policy is placed.",
  "policy-fascist": "A tense descending chord when a Fascist policy is placed.",
  "power-investigate": "A glassy inspection pulse when Investigate Loyalty begins.",
  "power-policy-peek": "A three-note shimmer when Policy Peek begins.",
  "power-special-election": "A brisk two-tone appointment signal when Special Election begins.",
  "power-execution": "A short gunshot-style noise when an execution resolves.",
  "victory-liberal": "A resolving civic chord when the Liberals win.",
  "victory-fascist": "A dark resolving chord when the Fascists win by policies.",
  "victory-hitler-elected": "A strong alarm chord when Hitler is elected Chancellor and the Fascists win.",
} as const;

export type SoundId = keyof typeof SOUND_CATALOG;

const SOUND_PREFERENCE_KEY = "secret-cabinet.sound-enabled";
let audioContext: AudioContext | null = null;

export function soundEnabled(): boolean {
  try {
    return window.localStorage.getItem(SOUND_PREFERENCE_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setSoundEnabled(enabled: boolean): void {
  try {
    window.localStorage.setItem(SOUND_PREFERENCE_KEY, enabled ? "on" : "off");
  } catch {
    // A blocked storage API must not block gameplay or audio.
  }
}

export function unlockAudio(): void {
  const context = getAudioContext();
  if (context) {
    void context.resume().catch(() => undefined);
  }
}

export function playSound(id: SoundId, delaySeconds = 0): void {
  if (!soundEnabled()) {
    return;
  }
  const context = getAudioContext();
  if (!context) {
    return;
  }
  const schedule = () => scheduleSound(context, id, Math.max(0, context.currentTime + delaySeconds));
  if (context.state === "suspended") {
    void context.resume().then(schedule).catch(() => undefined);
    return;
  }
  schedule();
}

export function useGameAudio(room: PublicRoomView): {
  muted: boolean;
  toggle: () => void;
} {
  const [muted, setMuted] = useState(() => !soundEnabled());
  const previous = useRef<AudioSnapshot | null>(null);

  useEffect(() => {
    const unlock = () => unlockAudio();
    window.addEventListener("pointerdown", unlock, { passive: true });
    window.addEventListener("keydown", unlock, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  useEffect(() => {
    const next = snapshotFor(room);
    const prior = previous.current;
    previous.current = next;
    if (!prior) {
      return;
    }

    const timers: number[] = [];
    if (isVoteRevealSnapshot(next)) {
      const isNewElection =
        next.ballotsTotal !== prior.ballotsTotal ||
        next.ballotsRevealed < prior.ballotsRevealed;
      const revealed = next.ballots.slice(isNewElection ? 0 : prior.ballotsRevealed);
      revealed.forEach((ballot, index) => {
        timers.push(window.setTimeout(() => playSound(ballot.ballot === "ja" ? "vote-ja" : "vote-nein"), index * 110));
      });
    }

    if (next.liberalPolicies > prior.liberalPolicies) {
      timers.push(window.setTimeout(() => playSound("policy-liberal"), 0));
    } else if (next.fascistPolicies > prior.fascistPolicies) {
      timers.push(window.setTimeout(() => playSound("policy-fascist"), 0));
    }

    if (next.phase !== prior.phase) {
      if (next.phase === "executive-investigate") timers.push(window.setTimeout(() => playSound("power-investigate"), 0));
      if (next.phase === "executive-policy-peek") timers.push(window.setTimeout(() => playSound("power-policy-peek"), 0));
      if (next.phase === "executive-special-election") timers.push(window.setTimeout(() => playSound("power-special-election"), 0));
    }

    if (next.lastExecutionId && next.lastExecutionId !== prior.lastExecutionId) {
      timers.push(window.setTimeout(() => playSound("power-execution"), 0));
    }

    if (next.winnerReason && next.winnerReason !== prior.winnerReason) {
      const sound = next.winnerReason === "hitler-elected"
        ? "victory-hitler-elected"
        : next.winnerParty === "liberal"
          ? "victory-liberal"
          : "victory-fascist";
      timers.push(window.setTimeout(() => playSound(sound), 0));
    }

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [room]);

  const toggle = useCallback(() => {
    setMuted((current) => {
      const next = !current;
      setSoundEnabled(!next);
      if (!next) unlockAudio();
      return next;
    });
  }, []);

  return { muted, toggle };
}

interface AudioSnapshot {
  phase: PublicRoomView["phase"];
  phaseResult: PublicRoomView["phaseResult"];
  ballotsRevealed: number;
  ballotsTotal: number;
  ballots: Array<{ playerId: string; ballot: "ja" | "nein" }>;
  liberalPolicies: number;
  fascistPolicies: number;
  lastExecutionId: string | null;
  winnerParty: PublicRoomView["winner"] extends infer Winner
    ? Winner extends { party: infer Party } ? Party : null
    : null;
  winnerReason: PublicRoomView["winner"] extends infer Winner
    ? Winner extends { reason: infer Reason } ? Reason : null
    : null;
}

function snapshotFor(room: PublicRoomView): AudioSnapshot {
  const ballots = isVoteRevealSnapshot({
    phase: room.phase,
    phaseResult: room.phaseResult,
    winnerReason: room.winner?.reason ?? null,
  })
    ? room.lastGovernment?.ballots ?? []
    : [];
  return {
    phase: room.phase,
    phaseResult: room.phaseResult,
    ballotsRevealed: room.lastGovernment?.ballotsRevealed ?? 0,
    ballotsTotal: room.lastGovernment?.ballotsTotal ?? 0,
    ballots,
    liberalPolicies: room.policies.liberal,
    fascistPolicies: room.policies.fascist,
    lastExecutionId: room.lastExecution?.playerId ?? null,
    winnerParty: room.winner?.party ?? null,
    winnerReason: room.winner?.reason ?? null,
  };
}

function isVoteRevealSnapshot(snapshot: Pick<AudioSnapshot, "phase" | "phaseResult" | "winnerReason">): boolean {
  return (
    (snapshot.phase === "election-result" && snapshot.phaseResult !== "veto-accepted") ||
    (snapshot.phase === "game-over" && snapshot.winnerReason === "hitler-elected")
  );
}

function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") {
    return null;
  }
  if (audioContext) {
    return audioContext;
  }
  const Context = window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Context) {
    return null;
  }
  try {
    audioContext = new Context();
    return audioContext;
  } catch {
    return null;
  }
}

function scheduleSound(context: AudioContext, id: SoundId, start: number): void {
  if (id === "power-execution") {
    scheduleNoise(context, start, 0.2);
    return;
  }
  const patterns: Record<Exclude<SoundId, "power-execution">, number[]> = {
    "vote-ja": [660, 880],
    "vote-nein": [220, 150],
    "policy-liberal": [440, 554, 660],
    "policy-fascist": [220, 180, 130],
    "power-investigate": [520, 780, 1_040],
    "power-policy-peek": [620, 830, 1_180],
    "power-special-election": [340, 520],
    "victory-liberal": [392, 523, 659, 784],
    "victory-fascist": [220, 185, 147],
    "victory-hitler-elected": [110, 165, 110, 220],
  };
  const frequencies = patterns[id];
  frequencies.forEach((frequency, index) => {
    const duration = id.startsWith("victory") ? 0.28 : 0.14;
    const at = start + index * (id.startsWith("victory") ? 0.16 : 0.09);
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = id.includes("fascist") || id.includes("nein") || id === "victory-hitler-elected" ? "sawtooth" : "triangle";
    oscillator.frequency.setValueAtTime(frequency, at);
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(id.startsWith("victory") ? 0.18 : 0.1, at + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(at);
    oscillator.stop(at + duration + 0.03);
  });
}

function scheduleNoise(context: AudioContext, start: number, duration: number): void {
  const buffer = context.createBuffer(1, Math.max(1, Math.floor(context.sampleRate * duration)), context.sampleRate);
  const data = buffer.getChannelData(0);
  for (let index = 0; index < data.length; index += 1) {
    data[index] = (Math.random() * 2 - 1) * (1 - index / data.length);
  }
  const source = context.createBufferSource();
  const filter = context.createBiquadFilter();
  const gain = context.createGain();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(1_400, start);
  gain.gain.setValueAtTime(0.24, start);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  source.buffer = buffer;
  source.connect(filter).connect(gain).connect(context.destination);
  source.start(start);
}
